/**
 * Chamadas HTTP das apólices de saúde.
 *
 * Papel: aqui ficam as chamadas de `/api/planos-saude` que a tela usa —
 * listar, criar, alterar e excluir uma apólice — e as três de
 * `/api/planos-saude/{id}/foto`, que baixam, enviam e apagam a foto da
 * carteirinha.
 *
 * Como usar: a listagem devolve o array inteiro, e não uma página. Nenhuma
 * transformação acontece aqui — o que sai é o que o schema validou, e a foto
 * sai como `Blob`, sem virar endereço.
 */
import {
  type NovaFotoDaCarteirinha,
  type NovoPlanoSaude,
  type PlanoSaude,
  planoSaudeSchema,
} from '@concierge/shared';
import { z } from 'zod';

import { clienteApi } from '@/shared/api';

/** A listagem inteira, sem envelope de paginação. */
const listaDePlanosSchema = z.array(planoSaudeSchema);

/**
 * Lê todas as apólices, por pessoa e, dentro dela, por plano.
 *
 * @param sinal - Sinal de cancelamento repassado pelo TanStack Query.
 * @returns As apólices cadastradas, cada uma com a pessoa aninhada.
 */
export function buscarPlanos(sinal?: AbortSignal): Promise<PlanoSaude[]> {
  return clienteApi.get('/planos-saude', { schema: listaDePlanosSchema, sinal });
}

/**
 * Cria uma apólice.
 *
 * A API responde 409 em dois casos que o formulário distingue pela mensagem:
 * aquela pessoa já tem aquela apólice, ou a pessoa escolhida deixou de existir
 * enquanto o formulário estava aberto.
 *
 * @param dados - A apólice preenchida, já validada por `novoPlanoSaudeSchema`.
 * @returns A apólice como ficou gravada, com a pessoa aninhada.
 */
export function criarPlano(dados: NovoPlanoSaude): Promise<PlanoSaude> {
  return clienteApi.post('/planos-saude', dados, { schema: planoSaudeSchema });
}

/**
 * Substitui todos os campos de uma apólice.
 *
 * @param id - Identificador da linha em `planos_saude`.
 * @param dados - A apólice inteira, e não apenas os campos alterados.
 * @returns A apólice como ficou gravada.
 */
export function atualizarPlano(id: string, dados: NovoPlanoSaude): Promise<PlanoSaude> {
  return clienteApi.put(`/planos-saude/${encodeURIComponent(id)}`, dados, {
    schema: planoSaudeSchema,
  });
}

/**
 * Apaga uma apólice.
 *
 * Não recebe schema porque a API responde 204 sem corpo.
 *
 * @param id - Identificador da linha em `planos_saude`.
 */
export function excluirPlano(id: string): Promise<void> {
  return clienteApi.remover(`/planos-saude/${encodeURIComponent(id)}`);
}

/**
 * Baixa a imagem da carteirinha de uma apólice.
 *
 * A rota exige o token, e por isso a imagem vem como `Blob`, e não como um
 * endereço a pôr no `src`. A API responde 404 quando a apólice não tem foto e
 * 503 quando o armazenamento de fotos não está configurado.
 *
 * @param id - Identificador da linha em `planos_saude`.
 * @param sinal - Sinal de cancelamento repassado pelo TanStack Query.
 * @returns Os bytes da imagem, com o tipo dela em `type`.
 */
export function buscarFotoDaCarteirinha(id: string, sinal?: AbortSignal): Promise<Blob> {
  return clienteApi.baixar(`/planos-saude/${encodeURIComponent(id)}/foto`, { sinal });
}

/**
 * Grava ou troca a foto da carteirinha de uma apólice.
 *
 * A apólice precisa existir antes: num plano novo, esta chamada só pode sair
 * depois de `criarPlano` responder com o identificador. A foto anterior é
 * apagada pela API depois que a nova fica gravada.
 *
 * @param id - Identificador da linha em `planos_saude`.
 * @param dados - A imagem em data URL, já validada por
 *   `novaFotoDaCarteirinhaSchema`.
 * @returns A apólice como ficou gravada, com `foto` preenchida.
 */
export function enviarFotoDaCarteirinha(
  id: string,
  dados: NovaFotoDaCarteirinha,
): Promise<PlanoSaude> {
  return clienteApi.put(`/planos-saude/${encodeURIComponent(id)}/foto`, dados, {
    schema: planoSaudeSchema,
  });
}

/**
 * Apaga a foto da carteirinha de uma apólice.
 *
 * Não recebe schema porque a API responde 204 sem corpo, e responde 204 também
 * quando a apólice já estava sem foto.
 *
 * @param id - Identificador da linha em `planos_saude`.
 */
export function excluirFotoDaCarteirinha(id: string): Promise<void> {
  return clienteApi.remover(`/planos-saude/${encodeURIComponent(id)}/foto`);
}
